import store from '@common/redux/store';
import { IAction } from '@common/types/';
import { Platform } from 'react-native';

export const sendEventAnalytics = async (action: IAction) => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') {
    return { status: 'SUCCEED' };
  }

  try {
    const {
      eventName = '',
      gtmEventName = '',
      gtmAdID = '',
      gtmPosition = '',
      gtmElemenText = '',
      gtmElementId = '',
    } = action.options || {};

    const gtag = window.gtag as any;
    const state: any = store.getState();
    const { email = '' } = state.auth.profile || {};

    if (typeof gtag === 'function') {
      gtag('event', `click_${eventName}`, {
        logged_user: email,
        platform: Platform.OS,
      });
    }

    if (gtmEventName) {
      getGTMActions({
        gtmEventName,
        gtmAdID,
        gtmElemenText,
        gtmElementId,
        gtmPosition,
      });
    }

    // Send Facebook Pixel event
    sendFacebookPixelEvent(eventName, action);

    return { status: 'SUCCEED' };
  } catch (error) {
    console.warn('Event analytics error:', error);
    return { status: 'SUCCEED' };
  }
};

/**
 * Generate or Get it from local data or session if existing
 */
const getGA4ClientId = (): string => {
  const CLIENT_ID_KEY = 'ga4_client_id';

  let clientId = getSessionData(CLIENT_ID_KEY) || getLocalData(CLIENT_ID_KEY);

  if (!clientId) {
    //GA4 format: timestamp.random_number
    const timestamp = Math.floor(Date.now() / 1000);
    const randomNum = Math.floor(Math.random() * 2147483647) + 1;
    clientId = `${timestamp}.${randomNum}`;
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(CLIENT_ID_KEY, clientId);
        localStorage.setItem(CLIENT_ID_KEY, clientId);
      }
    } catch (error) {
      console.warn('Failed to store client_id:', error);
    }
  }

  return clientId;
};

const getLocalData = (key: string): string | null => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const getSessionData = (key: string): string | null => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const getUrlParams = (url: string, name: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get(name);
  } catch (e) {
    return null;
  }
};

const getBrowserId = (): string => {
  let browserId = getSessionData('browser_id');
  if (!browserId) {
    browserId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        sessionStorage.setItem('browser_id', browserId);
      }
    } catch (error) {
      // Silent fail
    }
  }
  return browserId;
};

const getBestUtmParam = (param: string): string | null => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const documentReferrer =
    typeof document !== 'undefined' ? document.referrer : '';

  return (
    getUrlParams(currentUrl, param) ||
    getUrlParams(documentReferrer, param) ||
    getSessionData(`utm_${param}`) ||
    null
  );
};

const getAllUrlParameters = (url: string): Record<string, string> => {
  const params: Record<string, string> = {};
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
  } catch (e) {
    // Fallback for invalid URLs
    const queryString = url.split('?')[1];
    if (queryString) {
      queryString.split('&').forEach((param) => {
        const [key, value] = param.split('=');
        if (key && value) {
          params[decodeURIComponent(key)] = decodeURIComponent(value);
        }
      });
    }
  }
  return params;
};

const getCustomEventParameters = (
  allParams: Record<string, string>
): Record<string, string> => {
  const standardParams = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
    'ad_id',
    'ref',
    'referrer',
  ];

  const customParams: Record<string, string> = {};

  Object.entries(allParams).forEach(([key, value]) => {
    if (!standardParams.includes(key.toLowerCase()) && value) {
      // Add event_param prefix for GA4 compatibility
      customParams[`event_param.${key}`] = value;
    }
  });

  return customParams;
};

export const initializePageTracking = (eventName: string = 'page_loads') => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  const clientId = getGA4ClientId();
  const currentUrl = window.location.href;
  const allUrlParams = getAllUrlParameters(currentUrl);
  const customEventParams = getCustomEventParameters(allUrlParams);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    client_id: clientId,
    timestamp: new Date().toISOString(),
    timestamp_micros: Date.now() * 1000,
    event_name: eventName,
    landing_page: getSessionData('landing_page') || currentUrl,
    ad_id: getUrlParams(currentUrl, 'ad_id') || 'PageLoadAdsSample',
    utm_source: getBestUtmParam('utm_source'),
    utm_medium: getBestUtmParam('utm_medium'),
    utm_campaign: getBestUtmParam('utm_campaign'),
    browser_id: getBrowserId(),
    platform: Platform.OS,
    user_type: 'anonymous',
    referrer: document.referrer,
    page_url: currentUrl,
    page_title: document.title,

    // Add custom parameters from URL
    ...customEventParams,
  });
};

export const getGTMActions = (data: any) => {
  if (data) {
    const { gtmEventName, gtmAdID, gtmElemenText, gtmElementId, gtmPosition } =
      data;
    const state: any = store.getState();
    const { email = '' } = state.auth.profile || {};
    const { isAuth } = state.auth;
    const userInfo = isAuth && { logged_user: email, platform: Platform.OS };

    const currentUrl =
      typeof window !== 'undefined' ? window.location.href : '';
    const documentReferrer =
      typeof document !== 'undefined' ? document.referrer : '';

    const currentUrlParams = getAllUrlParameters(currentUrl);
    const referrerUrlParams = getAllUrlParameters(documentReferrer);

    const allUrlParams = { ...referrerUrlParams, ...currentUrlParams };

    const customEventParams = getCustomEventParameters(allUrlParams);

    const clientId = getGA4ClientId();

    window.dataLayer = window.dataLayer || [];

    const gtmData = {
      event: gtmEventName,
      client_id: clientId,
      timestamp: new Date().toISOString(),
      timestamp_micros: Date.now() * 1000,
      event_name: gtmEventName,
      user_id: isAuth ? email : undefined,

      // Session information
      landing_page: getSessionData('landing_page') || currentUrl,
      ad_id:
        gtmAdID || getUrlParams(currentUrl, 'ad_id') || getSessionData('ad_id'),
      utm_source: getBestUtmParam('utm_source'),
      utm_medium: getBestUtmParam('utm_medium'),
      utm_campaign: getBestUtmParam('utm_campaign'),
      utm_term: getBestUtmParam('utm_term'),
      utm_content: getBestUtmParam('utm_content'),

      // Element information
      ...(gtmElementId || gtmElemenText
        ? {
            'event_param.click_text': gtmElemenText,
            'event_param.click_id': gtmElementId,
            'event_param.click_position': gtmPosition,
          }
        : {}),

      // Custom URL parameters (dynamically added)
      ...customEventParams,

      // User information
      browser_id: getBrowserId(),
      logged_user: isAuth ? email : undefined,
      platform: Platform.OS,
      user_type: isAuth ? 'authenticated' : 'anonymous',

      'user_property.user_type': isAuth ? 'authenticated' : 'anonymous',
      'user_property.platform': Platform.OS,
      ...(isAuth
        ? {
            'user_property.logged_user': email,
            user_info: userInfo,
          }
        : {}),

      referrer: documentReferrer,
      page_url: currentUrl,
      page_title: typeof document !== 'undefined' ? document.title : '',

      'event_param.page_location': currentUrl,
      'event_param.page_title':
        typeof document !== 'undefined' ? document.title : '',
      'event_param.page_referrer': documentReferrer,
    };

    window.dataLayer.push(gtmData);

    // Store initial data on first visit
    if (typeof window !== 'undefined') {
      try {
        if (!getSessionData('landing_page')) {
          sessionStorage.setItem('landing_page', currentUrl);
        }

        const adId = getUrlParams(currentUrl, 'ad_id');
        if (adId && !getSessionData('ad_id')) {
          sessionStorage.setItem('ad_id', adId);
        }

        // Store UTM parameters if they exist in URL
        ['source', 'medium', 'campaign', 'term', 'content'].forEach((param) => {
          const value = getUrlParams(currentUrl, `utm_${param}`);
          if (value && !getSessionData(`utm_${param}`)) {
            sessionStorage.setItem(`utm_${param}`, value);
          }
        });

        Object.entries(allUrlParams).forEach(([key, value]) => {
          const storageKey = `custom_param_${key}`;
          if (!getSessionData(storageKey)) {
            sessionStorage.setItem(storageKey, value);
          }
        });
      } catch (error) {
        console.warn('Failed to store URL parameters:', error);
      }
    }
  }
};

/**
 * Send Facebook Pixel event with enhanced parameters
 */
export const sendFacebookPixelEvent = (eventName: string, action: IAction) => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  if (typeof window.fbq !== 'function') return;

  // Get appropriate Facebook Pixel standard event
  const customEventName = action.options?.eventName || eventName;
  const fbPixelEvent = getFacebookPixelStandardEvent(
    action.actionType,
    customEventName
  );
  const customData: any = {};
  if (action.options) {
    const {
      gtmAdID,
      gtmElemenText,
      gtmElementId,
      gtmPosition,
      fbPixelValue,
      fbPixelCurrency,
      fbPixelContentIds,
      fbPixelContentType,
      fbPixelContents,
      fbPixelNumItems,
      fbPixelContentName,
      fbPixelContentCategory,
      fbPixelStatus,
      formData,
    } = action.options;

    // Standard tracking parameters
    if (gtmAdID) customData.ad_id = gtmAdID;
    if (gtmElemenText) customData.element_text = gtmElemenText;
    if (gtmElementId) customData.element_id = gtmElementId;
    if (gtmPosition) customData.position = gtmPosition;

    if (fbPixelValue !== undefined && fbPixelValue > 0) {
      customData.value = parseFloat(fbPixelValue);
      if (fbPixelCurrency) customData.currency = fbPixelCurrency;
    }
    if (fbPixelContentName) customData.content_name = fbPixelContentName;
    if (fbPixelContentCategory)
      customData.content_category = fbPixelContentCategory;
    if (fbPixelStatus !== undefined) customData.status = fbPixelStatus;
    if (fbPixelContentIds)
      customData.content_ids = Array.isArray(fbPixelContentIds)
        ? fbPixelContentIds
        : [fbPixelContentIds];
    if (fbPixelContentType) customData.content_type = fbPixelContentType;
    if (fbPixelContents) customData.contents = fbPixelContents;
    if (fbPixelNumItems) customData.num_items = parseInt(fbPixelNumItems) || 1;

    // Include form data if available
    if (formData) {
      customData.form_data = formData;
      // Spread form data properties directly into customData
      Object.keys(formData).forEach((key) => {
        if (!customData[key]) {
          // Don't override existing properties
          customData[key] = formData[key];
        }
      });
    }
  }

  try {
    window.fbq('track', fbPixelEvent, customData);

    // Also send to GTM dataLayer for cross-platform tracking
    const gtmEventName = customEventName || eventName;
    if (window.dataLayer) {
      const gtmData: any = {
        event: gtmEventName,
        fbPixelEventName: fbPixelEvent,
        timestamp: new Date().toISOString(),
      };

      // Add form data to GTM
      if (action.options?.formData) {
        // Include only the complete form data object (no individual keys)
        gtmData.form_data = action.options.formData;
      }

      window.dataLayer.push(gtmData);
    }
  } catch (error) {
    console.warn('Tracking setup error:', error);
  }
};

/**
 * Simple Facebook Pixel trigger function for SubmitApplication events
 */
export const triggerFacebookPixelWithGTM = (
  eventType: string,
  eventData: any = {}
) => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') return;

  if (typeof window.fbq !== 'function') return;

  try {
    // Send to Facebook Pixel
    window.fbq('track', eventType, eventData);

    // Send to GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventType,
        fbPixelEventName: eventType,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (error) {}
};

/**
 * Send form submission events to both GTM and Facebook Pixel
 */
export const sendFormSubmissionTracking = (
  action: IAction,
  formData: any = {},
  actualFormValues: any = {},
  customEventName?: string
) => {
  try {
    const state: any = store.getState();
    if (!state) {
      return;
    }
    const { email = '' } = state.auth?.profile || {};
    const { isAuth } = state.auth || {};
    const submissionData = {
      form_id: action.options?.formId || action.id || 'unknown_form',
      submission_timestamp: new Date().toISOString(),
      user_type: isAuth ? 'authenticated' : 'anonymous',
      logged_user: isAuth ? email : undefined,
      platform: Platform.OS,
      action_type: action.actionType || 'unknown_action',
      ...formData,
      ...Object.keys(actualFormValues || {}).reduce((acc, key) => {
        const value = actualFormValues[key];
        if (typeof value === 'object' && value !== null) {
          // Handle nested objects by stringifying them
          acc[`field_${key}`] = JSON.stringify(value);
        } else {
          acc[`field_${key}`] = value;
        }
        return acc;
      }, {} as any),

      // Also include raw form values without prefix for backward compatibility
      form_fields: actualFormValues || {},
      form_fields_count: Object.keys(actualFormValues || {}).length,
    };

    // Send to Google Analytics via gtag
    sendGtagFormSubmission(submissionData, action, customEventName);

    // Send to Facebook Pixel
    sendFacebookPixelFormSubmission(submissionData, action, customEventName);
  } catch (error) {
    console.warn('Form submission tracking error:', error);
  }
};

/**
 * Send form submission to Google Analytics gtag
 */
export const sendGtagFormSubmission = (
  formData: any,
  action: IAction,
  customEventName?: string
) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const state: any = store.getState();
    if (!state) {
      return;
    }

    const { email = '' } = state.auth?.profile || {};
    const { isAuth } = state.auth || {};
    const eventName =
      customEventName || action.options?.eventName || 'form_submission';

    const eventData = {
      event_category: 'form',
      event_label: customEventName,
      form_id: formData.form_id,
      user_type: formData.user_type,
      ...(isAuth && { logged_user: email }),
      ...formData,
    };

    if (window.dataLayer) {
      const dataLayerEvent = {
        event:
          eventName?.replace(/\s+/g, '_').toLowerCase() || 'form_submission',
        eventCategory: 'form',
        eventAction: 'submit',
        eventLabel: eventName,

        // Form-specific data (flattened for GTM variables)
        form_id: formData.form_id,
        submission_type: formData.submission_type,
        user_type: formData.user_type,
        platform: formData.platform,
        success: formData.success,
        submission_timestamp: formData.submission_timestamp,

        // User data (if available)
        ...(isAuth && { logged_user: email }),
        ...(formData.user_id && { user_id: formData.user_id }),
        ...(formData.database_id && { database_id: formData.database_id }),
        ...(formData.record_id && { record_id: formData.record_id }),
        ...(formData.email && { user_email: formData.email }),

        ...Object.keys(formData).reduce((acc, key) => {
          const standardKeys = [
            'form_id',
            'submission_type',
            'user_type',
            'platform',
            'success',
            'submission_timestamp',
            'user_id',
            'database_id',
            'record_id',
            'email',
            'logged_user',
            'action_type',
          ];
          if (!standardKeys.includes(key)) {
            // Handle nested objects in form data
            const value = formData[key];
            if (
              typeof value === 'object' &&
              value !== null &&
              !Array.isArray(value)
            ) {
              Object.keys(value).forEach((nestedKey) => {
                acc[`form_${key}_${nestedKey}`] = value[nestedKey];
              });
            } else {
              acc[`form_${key}`] = value;
            }
          }
          return acc;
        }, {} as any),
        formData: eventData,
      };

      window.dataLayer.push(dataLayerEvent);
    }
    // Also send via gtag if available
    if (typeof window.gtag === 'function') {
      const gtagEventName =
        customEventName?.replace(/\s+/g, '_').toLowerCase() || 'form_submit';
      window.gtag('event', gtagEventName, eventData);
    }
  } catch (error) {
    console.warn('GTM form submission error:', error);
  }
};

/**
 * Get Facebook Pixel standard event based on action type
 */
const getFacebookPixelStandardEvent = (
  actionType: string,
  _customEventName?: string
): string => {
  switch (actionType) {
    case 'signup':
      return 'CompleteRegistration';
    case 'createObject':
    case 'create':
      return 'Lead';
    default:
      return 'Lead';
  }
};

export const sendFacebookPixelFormSubmission = (
  formData: any,
  action: IAction,
  customEventName?: string
) => {
  if (typeof window === 'undefined' || Platform.OS !== 'web') {
    return;
  }

  try {
    if (typeof window.fbq !== 'function') {
      return;
    }

    const state: any = store.getState();
    if (!state) {
      return;
    }

    // Get appropriate Facebook Pixel standard event
    const eventName = customEventName || action.options?.eventName;
    const fbPixelEvent = getFacebookPixelStandardEvent(
      action.actionType,
      eventName
    );

    const pixelData = {
      content_name: fbPixelEvent,
      form_id: formData.form_id,
      user_type: formData.user_type,
      custom_event_name: customEventName,
      ...formData,
    };

    window.fbq('track', fbPixelEvent, pixelData);
    const gtmEventName =
      customEventName || formData.action_type || 'form_submission';
    if (window.dataLayer) {
      const fbDataLayerEvent = {
        event: gtmEventName,
        fbPixelEventName: fbPixelEvent,
        fbPixelData: pixelData,
        formSubmissionData: formData,
        eventCategory: 'facebook_pixel',
        eventAction: 'submit_application',
        eventLabel: eventName,
        timestamp: new Date().toISOString(),
      };
      delete fbDataLayerEvent.fbPixelData;
      window.dataLayer.push(fbDataLayerEvent);
    }
  } catch (error) {
    console.warn('Facebook Pixel form submission error:', error);
  }
};

/**
 * Facebook Pixel Standard Events - SubmitApplication
 * Based on: https://business.facebook.com/business/help/402791146561655
 */
export const fbPixelStandardEvents = {
  // SubmitApplication - A person submitted an application
  submitApplication: (data: any = {}) => {
    triggerFacebookPixelWithGTM('SubmitApplication', data);
  },
};

export const fbPixelTriggers = fbPixelStandardEvents;
